<template>
  <div class="file-assets-root">
    <!-- Only show this top bar when in 'normal' mode -->
    <template v-if="fileMode === FileMode.NORMAL">
      <pui-flex direction="row" flex="1" class="file-assets-top-content">
        <pui-flex
          direction="row"
          alignItems="center"
          justifyContent="end"
          flex="1"
          class="file-assets-top-content__folder-config"
        >
          <pui-menu-button
            ref="folderSorts"
            :picto="selectedFolderSort ? selectedFolderSort.picto : ''"
            class="folder-sort-menu-button"
          >
            <pui-flex direction="column" class="folder-sort-options">
              <pui-button
                v-for="sort in FOLDER_SORTS"
                :key="`${sort.sortKey}.${sort.direction}`"
                :picto="sort.picto"
                :title="$t(`dam.folders.tree.sort.${sort.i18nKey}.${sort.direction}`)"
                @click="onClickFolderSort(sort)"
                block
                flat
                variant="secondary"
                class="folder-sort-btn"
              >
                {{ $t(`dam.folders.tree.sort.${sort.i18nKey}.${sort.direction}`) }}
              </pui-button>
            </pui-flex>
          </pui-menu-button>
          <pui-menu-button
            v-if="[Permissions.Folders.CREATE].some(p => hasPermission(p))"
            class="folder-actions-menu-button"
          >
            <pui-flex direction="column" class="folder-action-options">
              <pui-button
                v-permission="Permissions.Folders.CREATE"
                @click="openFolderCreateDialog(null)"
                block
                flat
                class="folder-action-btn"
              >
                {{ $t('dam.folders.actions.create_root_folder') }}
              </pui-button>
              <!-- TODO: uncomment this when move folder is implemented -->
              <!-- <pui-button
              @click="openFolderMoveDialog(moveableFolders)"
              :disabled="!moveableFolders.length"
              block
              flat
            >
              {{ $tc('dam.folders.actions.move_selected_folder', moveableFolders.length) }}
            </pui-button> -->
            </pui-flex>
          </pui-menu-button>
        </pui-flex>
        <pui-file-search
          ref="fileSearch"
          v-if="fileMode === FileMode.NORMAL"
          :generateSearchQuery="generateSearchQuery"
          @filtersOpened="searchFiltersOpened"
          @filtersClosed="searchFiltersClosed"
          @searchClicked="triggerSearchForced"
          @inputEnter="triggerSearchForced"
          @clearSelection="triggerSearchForced"
          @blur="triggerSearchIfDirty"
          @onRemoveItem="onSearchItemRemoved"
          @executeConfirmableInfoAction="executeConfirmableInfoAction"
          @searchItemsChange="onSearchItemsChange"
          class="assets-search-root"
        />
        <pui-flex direction="row" flex="1" class="file-assets-top-content__actions">
          <pui-button
            v-if="fileMode == FileMode.NORMAL"
            :disabled="!selectedFiles.length"
            @click="toggleShowSelectionPanel"
            :variant="showSelectionPanel ? 'primary' : 'secondary'"
            picto="mdi-image-multiple"
            class="assets__action-button assets__my-selection-button"
          >
            {{ $t('dam.assets.my_selection', { count: selectedFileCount }) }}
          </pui-button>
          <pui-button
            v-if="fileMode == FileMode.NORMAL"
            :disabled="!selectedFiles.length"
            @click="resetSelectedItems"
            class="assets__action-button assets__reset-selection-button"
            variant="secondary"
            outline
          >
            {{ $t('dam.assets.reset_selection') }}
          </pui-button>
        </pui-flex>
      </pui-flex>
    </template>

    <div class="file-assets-content">
      <!-- FOLDERS TREE -->
      <pui-side-panel
        :open="showLeftPanel"
        :hideButton="showLeftPanel"
        :class="leftPanelClass"
        @toggleOpen="toggleLeftPanel"
        mode="width"
        openToSide="right"
        openWidth="300px"
        buttonWidth="40px"
      >
        <pui-flex direction="column" class="file-assets-panel-left-content">
          <pui-flex class="assets-panel-left-folder-tree-wrapper">
            <pui-spinner v-if="isExpandingToUploadedFolder" position="fixed" />
            <pui-folder-tree
              ref="folderTree"
              v-scrollable.y
              :options="{
                checkbox: true,
                dndDrag: true,
                dndDrop: true,
                hoverState: true,
              }"
              @selectionChange="onFolderSelectionChange"
              @selectionStateChange="onFolderSelectionStateChange"
              @error="handleTreeError"
              @dndStart="handleTreeDragStart"
              @dndEnd="handleTreeDragEnd"
              @dndDrop="handleTreeDrop"
              :getRoots="getDamRootFolders"
              :treeProps="{
                dragOverCb: onTreeDragOver,
                dragEnterCb: onTreeDragEnter,
              }"
              :sort="selectedFolderSort"
              class="assets-panel-left-folder-tree-root"
            >
              <template #afterNameAfterMeta="{ node, isHovering }">
                <template v-if="fileMode === FileMode.SELECTION">
                  <pui-folder-node-selection-count :count="getFolderSelectionCount(node)" />
                </template>
                <template
                  v-else-if="
                    [Permissions.Folders.CREATE].some(p => hasPermission(p)) &&
                      (isHovering || node._isPopperOpen)
                  "
                >
                  <pui-menu-button
                    :popperProps="{
                      contentClickStopPropagation: true,
                      referenceTagName: 'div',
                      options: {
                        modifiers: [{ name: 'offset', options: { offset: [-2, 0] } }],
                      },
                    }"
                    :popperListeners="nodeActionPopperListeners(node)"
                    class="node-actions-menu-button"
                    stopPropagation
                  >
                    <pui-flex direction="column" class="node-actions">
                      <!-- TODO: uncomment this when move folder is implemented -->
                      <!-- <pui-button block flat>
                      <i18n
                        @click="openFolderMoveDialog([node])"
                        path="dam.folders.buttons.move"
                      ></i18n>
                    </pui-button> -->
                      <pui-button
                        @click="openFolderCreateDialog(node)"
                        v-permission="Permissions.Folders.CREATE"
                        block
                        flat
                      >
                        {{ $t('dam.folders.actions.create_sub_folder') }}
                      </pui-button>
                    </pui-flex>
                  </pui-menu-button>
                </template>
              </template>
            </pui-folder-tree>
          </pui-flex>
        </pui-flex>
      </pui-side-panel>

      <!-- GALLERY -->
      <pui-gallery
        ref="gallery"
        :items="filteredItems"
        :getItemId="getItemId"
        :getItemDimensions="getItemDimensions"
        :loading="loadingFiles"
        :initialMode="galleryMode"
        :trimPoolOnItemsChange="true"
        :hasMoreItems="hasMoreFiles"
        :hasError="loadFilesError"
        :overscanRowCount="4"
        :dataComponent="puiFileGalleryItem"
        :dataCallbackProps="fileGalleryItemCallbackProps"
        :dataCallbackListeners="fileGalleryItemCallbackListeners"
        :gridProps="{ offsetHeight: FILE_GALLERY_ITEM_META_HEIGHT_PX }"
        @requestMoreItems="onGalleryRequestMoreItems"
        @changeMode="onGalleryModeChanged"
        class="file-assets-gallery"
        virtual
        virtualMode="pool"
      >
        <template #configLeft>
          <div class="file-assets-selection assets-gallery-config">
            <div>
              {{
                $tc('dam.assets.file_count', nbTotalItems, {
                  count: nbTotalItems,
                })
              }}
            </div>
          </div>
        </template>
        <template v-if="searchSorts.length > 1" #config-right-start>
          <pui-menu-button
            ref="searchSorts"
            :picto="selectedSearchSort ? selectedSearchSort.picto : ''"
          >
            <pui-flex direction="column" class="assets-gallery-sort">
              <pui-button
                v-for="sort in searchSorts"
                :key="`${sort.key}.${sort.direction}`"
                :picto="sort.picto"
                :title="$t(`dam.files.search.sort.${sort.key}.${sort.direction}`)"
                @click="onClickFilesSort(sort)"
                block
                flat
                variant="secondary"
                class="assets-gallery-sort-btn"
              >
                {{ $t(`dam.files.search.sort.${sort.key}.${sort.direction}`) }}
              </pui-button>
            </pui-flex>
          </pui-menu-button>
        </template>
        <template #error>
          <pui-flex direction="column" alignItems="center">
            <i18n path="dam.files.gallery.error.primary" />
            <div @click="loadData" class="file-assets-gallery-error-reload">
              <i18n path="dam.files.gallery.error.reload" />
            </div>
          </pui-flex>
        </template>

        <template #noResults>
          <pui-flex direction="column" class="file-assets-gallery-no-results">
            <i18n
              v-if="!folderSelection.length && !searchItems.length"
              path="dam.files.gallery.hint_apply_filters"
            />
            <i18n
              v-else-if="folderSelection.length && !searchItems.length"
              path="dam.files.gallery.with_folders_no_results"
            />
            <template v-else>
              <i18n path="dam.files.gallery.with_filters_no_results.primary"></i18n>
              <ul>
                <li><i18n path="dam.files.gallery.with_filters_no_results.hint_1"></i18n></li>
                <li><i18n path="dam.files.gallery.with_filters_no_results.hint_2"></i18n></li>
              </ul>
            </template>
          </pui-flex>
        </template>
      </pui-gallery>

      <pui-file-drag-icon
        ref="selectionDragIcon"
        :count="selectionDragCount"
        style="visibility: hidden; position: absolute; top: -1000px;"
      ></pui-file-drag-icon>

      <pui-file-drag-icon
        ref="singleDragIcon"
        :count="1"
        style="visibility: hidden; position: absolute; top: -1000px;"
      ></pui-file-drag-icon>

      <pui-folder-drag-icon
        ref="folderDragIcon"
        style="visibility: hidden; position: absolute; top: -1000px;"
      ></pui-folder-drag-icon>

      <!--SIDE PANEL FOR SELECTED/INFO ITEMS-->
      <pui-file-details-panel
        ref="detailsPanel"
        :infoFiles="selectedFilesForInfos"
        :selectionFiles="selectedFiles"
        :showSelectionPanel="showSelectionPanel"
        :showInfoPanel="showInfoPanel"
        :isExpanded="showRightPanel"
        :openWidth="DETAIL_PANEL_WIDTH_OPEN"
        :closedWidth="DETAIL_PANEL_WIDTH_CLOSED"
        :hideOpenSelectionDialog="fileMode === FileMode.SELECTION"
        :draggingFolder="draggingFolder"
        @toggleOpen="toggleRightPanel"
        @openSelectionDialog="openSelectionDialog"
        class="file-assets-content__right"
      />
    </div>

    <!-- ZOOMED ITEM DIALOG -->
    <pui-file-zoom-dialog ref="previewAssetDialog" @changeZoomedItem="onChangeZoomedItem" />

    <!-- FILE ASSOCIATE DIALOG -->
    <pui-file-associate-dialog ref="associateDialog" />

    <pui-dam-file-upload-dialog ref="fileUploadDialog" />
    <pui-folder-create-dialog ref="folderCreateDialog" @createdFolder="onFolderCreated" />
    <!-- <pui-folder-move-dialog ref="folderMoveDialog" @triggerMove="executeFolderMove" /> -->
  </div>
</template>

<script>
import { GalleryMode } from 'piivo-ui/src/components/gallery/Gallery';
import Vue from 'vue';

import services from '../../../../core/services';
import { base64EncodeUnicode } from '../../../../utils/string';
import { DAM_APP_NAME } from '../../constants';
import {
  FILE_GALLERY_ITEM_META_HEIGHT_PX,
  FileGalleryEvents,
  FileUploadEvents,
} from '../../constants/file';
import { FOLDER_SORTS } from '../../constants/folder';
import {
  CHECKED_ALL_CHILDREN_STATES,
  CHECKED_STATES,
  SelectionState,
} from '../../constants/folderTree';
import Permissions from '../../constants/permissions';
import { Settings, USER_SETTINGS_DAM } from '../../constants/userSettings';
import { generateFilesPiiQLQuery } from '../../helpers/fileQueryGenerator';
import { extractBorderColorAttributes } from '../../helpers/fileSearchHelper';
import { getDamService } from '../../services';
import { createFindByItemId } from '../../utils/files';
import { getFolderIdFromPath, getFolderIdsFromPath } from '../../utils/folders';
import puiFolderCreateDialog from '../folders/FolderCreateDialog';
import puiFolderDragIcon from '../folders/FolderDragIcon';
import puiFolderNodeSelectionCount from '../folders/FolderNodeSelectionCount';
import puiDamFileUploadDialog from './DamFileUploadDialog';
import puiFileDragIcon from './FileDragIcon';
import puiFileGalleryItem from './FileGalleryItem';
import puiFileZoomDialog from './FileZoomDialog';
import puiFolderTree from './FolderTree';
import puiFileDetailsPanel from './info/FileDetailsPanel';
import puiFileAssociateDialog from './move/FileAssociateDialog';
import puiFileSearch from './search/FileSearch';

const FILE_SERVICE_NAMESPACE = 'assets';

export const FileMode = {
  NORMAL: 'normal',
  SELECTION: 'selection',
};

const PanelTypes = {
  INFO: 'information',
  ZOOM: 'zoom',
  SELECTION: 'selection',
};

const DETAIL_PANEL_WIDTH_OPEN = '1000px';
const DETAIL_PANEL_WIDTH_CLOSED = '360px';

const DRAG_ICON_DIM = 48;

export default {
  components: {
    puiFolderCreateDialog,
    puiDamFileUploadDialog,
    puiFileDetailsPanel,
    puiFileGalleryItem,
    puiFolderTree,
    puiFileAssociateDialog,
    puiFileZoomDialog,
    puiFileSearch,
    puiFolderNodeSelectionCount,
    puiFileDragIcon,
    puiFolderDragIcon,
  },
  props: {
    /**
     * Display mode
     */
    fileMode: {
      type: String,
      default: FileMode.NORMAL,
    },
    /**
     * Array of files to use as selection
     */
    forcedSelectedFiles: {
      type: Array,
      default: () => [],
    },
    /**
     * Array of files to display in the gallery
     */
    forcedFiles: {
      type: Array,
      default: () => [],
    },
    /**
     * The number of selected files in the original
     * assets view
     */
    initialSelectedFileCount: {
      type: Number,
      default: null,
    },
  },
  data() {
    let searchConfig = services
      .getService('applications')
      .getAppDeepParameter(DAM_APP_NAME, 'screensSettings', 'assets.search', true);
    if (searchConfig instanceof Error || !searchConfig) {
      searchConfig = { defaultSort: null, sorts: [] };
    } else if (searchConfig) {
      searchConfig = {
        defaultSort: null,
        sorts: [],
        ...searchConfig,
      };
    }

    let selectedSearchSort = null;
    let defaultSearchSort = null;
    if (searchConfig) {
      defaultSearchSort = !searchConfig.defaultSort
        ? null
        : searchConfig.sorts.find(
            sort =>
              sort.key === searchConfig.defaultSort.key &&
              sort.direction === searchConfig.defaultSort.direction
          );
      if (defaultSearchSort) {
        selectedSearchSort = defaultSearchSort;
      } else {
        selectedSearchSort = searchConfig.sorts[0];
      }
    }

    return {
      // GALLERY STATE
      loadingFiles: false,
      loadFilesError: false,
      pageSize: 60,
      nbTotalItems: 0,
      items: [...this.forcedFiles], // Load from prop on mount
      // SELECTION
      selectedFilesForInfos: [],
      selectedFiles: [...this.forcedSelectedFiles], // Load from prop on mount
      // Show selection panel by default in selection mode
      showSelectionPanel: this.fileMode === FileMode.SELECTION,
      showInfoPanel: false,
      // ITEMS OTHER
      draggingFiles: [],
      infoItemId: null,
      zoomedItemId: null,
      GalleryMode,
      puiFileGalleryItem,
      FILE_GALLERY_ITEM_META_HEIGHT_PX,
      selectionDragIconDiv: null,
      singleDragIconDiv: null,
      // OTHER
      showLeftPanel: true,
      // SEARCH
      searchItems: [],
      searchFiltersAreOpen: false,
      isSearchDirty: false,
      // Folders
      folderSelection: [],
      folderSelectionState: {},
      folderDragIconDiv: null,
      draggingFolder: null,
      selectedFolderSort: FOLDER_SORTS[0],
      FOLDER_SORTS,
      folderCreateDialogTitle: '',
      // Queries
      globalSearchQuery: '',
      // Details panel
      showRightPanel: false,
      DETAIL_PANEL_WIDTH_CLOSED,
      DETAIL_PANEL_WIDTH_OPEN,
      // OTHER
      FileMode,
      // UPLOAD
      isExpandingToUploadedFolder: false,
      searchConfig,
      selectedSearchSort,
      defaultSearchSort,
      // CONSTANTS
      Permissions,
    };
  },
  computed: {
    /**
     * @returns {object} - all files sorts configured in application
     */
    searchSorts() {
      return this.searchConfig.sorts || [];
    },
    /**
     * @returns {string} the first gallery mode that should be displayed
     */
    galleryMode() {
      return (
        services
          .getService('userSettings')
          .getInCategory(USER_SETTINGS_DAM, Settings.GALLERY_MODE) || 'grid'
      );
    },
    /**
     * @returns {Boolean} if we can request more files from the apiFiles
     */
    hasMoreFiles() {
      return this.items.length < this.nbTotalItems;
    },
    leftPanelClass() {
      return {
        'file-assets-content__left': true,
        open: this.showLeftPanel,
      };
    },
    infoItem() {
      return this.items.find(item => item.itemId === this.infoItemId);
    },
    /**
     * @returns {object[]} all our items but filtered according to the current
     * folder selection
     */
    filteredItems() {
      if (this.fileMode === FileMode.NORMAL) {
        return this.items;
      }
      if (this.fileMode === FileMode.SELECTION) {
        const hasFolderFilter = Object.values(this.folderSelectionState).some(state =>
          CHECKED_STATES.includes(state)
        );

        // If there is no filter, return all items
        if (!hasFolderFilter) {
          return this.items;
        }

        const filteredItems = [];

        // For each item, check if it is in the filter
        this.items.forEach(item => {
          let isInSelectedFolder = false;

          // Get all the folders from the item's paths
          const itemFolderIds = item.paths.reduce((allFolderIds, path) => {
            const folderIds = getFolderIdsFromPath(path);
            allFolderIds.push(...folderIds);
            return allFolderIds;
          }, []);

          let folderId = itemFolderIds.shift();

          // Iterate over all the folders of the item's paths until we can tell
          // whether or not one of the folders leading to the item is selected
          while (!isInSelectedFolder && folderId) {
            if (CHECKED_STATES.includes(this.folderSelectionState[folderId])) {
              isInSelectedFolder = true;
            }

            folderId = itemFolderIds.shift();
          }

          if (isInSelectedFolder) {
            filteredItems.push(item);
          }
        });

        return filteredItems;
      }

      return this.items;
    },
    /**
     * @returns {number} the number of selected files in the main assets view
     */
    selectedFileCount() {
      if (this.initialSelectedFileCount !== null) {
        return this.initialSelectedFileCount;
      }

      return this.selectedFiles.length;
    },
    /**
     * @returns {string} the drag count to show in the selection drag preview
     */
    selectionDragCount() {
      return this.selectedFiles.length > 99 ? '99+' : this.selectedFiles.length.toString();
    },
  },
  watch: {
    /**
     * Updates our data selectedFiles to match the prop forcedSelectedFiles
     *
     * @param {object} nextValue - the new value of selected files
     * @returns {void}
     */
    forcedSelectedFiles(nextValue) {
      this.selectedFiles = [...nextValue];
    },
    forcedFiles: {
      /**
       * Updates our data items to match the prop forcedFiles
       *
       * @param {object} nextValue - the new value of files
       * @returns {void}
       */
      handler(nextValue) {
        this.items = nextValue;

        // Also set the total number of items
        this.nbTotalItems = nextValue.length;
      },
      immediate: true, // Run on mount
    },
    /**
     * Closes the selection panel when the info panel is opened
     *
     * @param {boolean} nextValue - if the info panel is open
     * @returns {void}
     */
    showInfoPanel(nextValue) {
      if (nextValue) {
        this.showSelectionPanel = false;
      }
    },
    /**
     * Closes the info panel when the selection panel is opened
     *
     * @param {boolean} nextValue - if the selection panel is open
     * @returns {void}
     */
    showSelectionPanel(nextValue) {
      if (nextValue) {
        this.showInfoPanel = false;
      }
    },
    /**
     * Watches 'selectionDragCount' to recreate the drag icon div
     */
    async selectionDragCount() {
      if (this.selectionDragIconDiv) {
        this.selectionDragIconDiv.parentNode.removeChild(this.selectionDragIconDiv);
      }

      // Wait for DragIcon to receive prop
      await this.$nextTick();
      this.selectionDragIconDiv = this.createDragIcon(this.$refs.selectionDragIcon);
    },
  },
  methods: {
    /**
     * @param {string} permission - name of the permission to check
     * @returns {boolean} if the user has the permission
     */
    hasPermission(permission) {
      return services.getService('auth').hasPermission(permission);
    },
    // FILTERS
    piivoTranslate(value) {
      return Vue.filter('piivoTranslate')(value);
    },
    // FILES
    /**
     * Reloads the search results, starting from the beginning
     * of the results
     *
     * @returns {void}
     */
    reloadSearchFromBeginning() {
      this.loadData(true);
    },
    /**
     * Reloads the search results with new sort
     * @param {Object} sort - Sort object which contains key and direction
     * @returns {void}
     *
     */
    onClickFilesSort(sort) {
      if (
        this.selectedSearchSort &&
        this.selectedSearchSort.key === sort.key &&
        this.selectedSearchSort.direction === sort.direction
      ) {
        return;
      }
      this.selectedSearchSort = sort;
      if (this.$refs.searchSorts) {
        this.$refs.searchSorts.close();
      }
      this.loadData(true);
    },
    /**
     * @param searchGroups - groups of search items from the dam search. Each group will have
     * its own boolean operator between its items
     * @returns {string} the PiiQL query for the search groups and folders
     */
    generateSearchQuery(searchGroups) {
      const treeRoots = this.$refs.folderTree.getTreeRoots();
      return generateFilesPiiQLQuery(searchGroups, treeRoots, this.folderSelectionState);
    },
    /**
     * Retrieves files from apiFiles, recomputes masonry dimensions and then
     * checks if the bottom of the masonry container is visible
     *
     * @returns {void}
     */
    async loadData(initialRequest = false) {
      // Gallery scrolledToBottom will emit on first mount, when we
      // haven't yet established a search. So there is nothing
      // to paginate. Cancel the request
      if (!initialRequest && (this.loadingFiles || !this.hasMoreFiles)) {
        return;
      }

      if (initialRequest) {
        this.items = [];
        this.nbTotalItems = 0;
        this.globalSearchQuery = this.generateSearchQuery([this.searchItems]);

        // Since we cleared the items, we have to clear the virtual pool
        this.$nextTick(() => {
          this.$refs.gallery.trimPool();
        });
      }
      if (!this.globalSearchQuery || this.globalSearchQuery === '') {
        this.items = [];
        this.nbTotalItems = 0;

        // Since we cleared the items, we have to clear the virtual pool
        this.$nextTick(() => {
          this.$refs.gallery.trimPool();
        });
        return;
      }
      this.loadFilesError = false;
      this.loadingFiles = true;

      const searchConfig = {
        top: this.pageSize,
        skip: this.items.length,
        filter: this.globalSearchQuery,
        attributes: extractBorderColorAttributes().join(','),
        sortBy: !this.selectedSearchSort
          ? null
          : `${this.selectedSearchSort.key} ${this.selectedSearchSort.direction}`,
      };

      try {
        const response = await getDamService('filesService').search(
          FILE_SERVICE_NAMESPACE,
          searchConfig
        );

        const { items, totalNumberOfItems } = response.body;

        this.nbTotalItems = totalNumberOfItems;
        this.items.push(...items);

        this.loadingFiles = false;
        this.isSearchDirty = false;
      } catch (err) {
        console.error(err);
        if (!Vue.http.isCancel(err)) {
          services.getService('alerts').alertError(this.$t('dam.assets.files.load.error'));

          this.loadingFiles = false;
          this.loadFilesError = true;
        }
      }
    },
    /**
     * Toggles the left panel and if will open,
     * auto closes the right panel
     *
     * @returns {void}
     */
    toggleLeftPanel() {
      this.showLeftPanel = !this.showLeftPanel;

      // Auto close right if open left
      if (this.showLeftPanel) {
        this.showRightPanel = false;
      }
    },
    /**
     * Toggles the right panel and sets the left panel
     * to have the opposite state
     *
     * @returns {void}
     */
    toggleRightPanel() {
      this.showRightPanel = !this.showRightPanel;

      // Close left on right open
      // and open left on close right
      this.showLeftPanel = !this.showRightPanel;
    },
    /**
     * @param {Object} item - the item to evaluate
     * @returns {String} the id of the item
     */
    getItemId(item) {
      return item.itemId;
    },
    /**
     * Triggered when the gallery scrolls to the bottom
     *
     * @returns {void}
     */
    onGalleryRequestMoreItems() {
      if (this.fileMode === FileMode.NORMAL) {
        this.loadData();
      }
    },
    fileGalleryItemCallbackProps(thumbnailProps) {
      return {
        ...thumbnailProps,
        applyPropDimensions: thumbnailProps._mode === GalleryMode.MASONRY,
        showMeta: thumbnailProps._mode === GalleryMode.GRID,
        objectFit: 'contain',
        checked: this.isItemChecked(thumbnailProps.item),
        isShowingInfo: this.isItemInfoShowed(thumbnailProps.item),
        isZoomed: this.isItemZoomed(thumbnailProps.item),
        isBeingDragged: this.isItemBeingDragged(thumbnailProps.item),
      };
    },
    fileGalleryItemCallbackListeners(thumbnailProps) {
      return {
        checkChange: (...args) => this.handleItemCheckChangeHook(thumbnailProps.item, ...args),
        clickInfo: (...args) => this.handleClickItemInfoHook(thumbnailProps.item, ...args),
        clickZoom: (...args) =>
          this.handleClickItemZoom(thumbnailProps.item, thumbnailProps.itemIndex, ...args),
        dragStart: (...args) => this.handleItemDragStart(thumbnailProps.item, ...args),
        dragEnd: (...args) => this.handleItemDragEnd(thumbnailProps.item, ...args),
      };
    },
    /**
     * Called when the gallery mode changed
     *
     * @param {string} mode - the new mode
     * @returns {void}
     */
    onGalleryModeChanged(mode) {
      services
        .getService('userSettings')
        .setInCategory(USER_SETTINGS_DAM, Settings.GALLERY_MODE, mode);
    },
    /**
     * @param {Object} item - the item to evaluate
     * @returns {Boolean} if this item is selected
     */
    isItemChecked(item) {
      return !!this.selectedFiles.find(createFindByItemId(item));
    },
    /**
     * @param {Object} item - the item to evaluate
     * @returns {Boolean} if the item's info is being showed
     */
    isItemInfoShowed(item) {
      return !!this.selectedFilesForInfos.find(createFindByItemId(item));
    },
    /**
     * @param {Object} item - the item to evaluate
     * @returns {Boolean} if the item's picture is showed zoomed in
     */
    isItemZoomed(item) {
      return this.zoomedItemId === this.getItemId(item);
    },
    /**
     * @param {object} item - the file to evaluate
     * @returns {boolean} of the item is being dragged
     */
    isItemBeingDragged(item) {
      return !!this.draggingFiles.find(createFindByItemId(item));
    },
    /**
     * Hook when an item's checked state
     * changes and apply functions to
     *
     * @param {Boolean} checked - if this item is now checked
     * @param {(ClickEvent|ChangeEvent)} event - the event
     * that triggered the change
     * @returns {void}
     */
    handleItemCheckChangeHook(item, checked, event) {
      // Clear the info items while checking for unsaved changes
      // We want to check the unsaved info individually of unsaved selection,
      // so that if we need to display a confirmation dialog, it will always
      // correspond to the current panel
      this.executeConfirmableAction(() => {
        this.resetSelectedInfoItems();

        // Then update our selection, while also checking for unsaved changes
        this.executeConfirmableAction(() => {
          this.handleItemCheckChange(item, checked, event);
        }, PanelTypes.SELECTION);
      }, PanelTypes.INFO);
    },
    /**
     * Updates the checked state when an item's checked state
     * changes
     *
     * @param {Boolean} checked - if this item is now checked
     * @param {(ClickEvent|ChangeEvent)} event - the event
     * that triggered the change
     * @returns {void}
     */
    handleItemCheckChange(item, checked, event) {
      const hasExistingSelection = this.selectedFiles.length;

      // Case with shift key pressed
      // Can only 'shift' from previous to current file, if there
      // is at least one selected item
      if (event && event.shiftKey && hasExistingSelection) {
        const lastSelectedItem = this.selectedFiles[this.selectedFiles.length - 1];
        const previousItemIndex = this.filteredItems.findIndex(
          createFindByItemId(lastSelectedItem)
        );
        const selectedItemIndex = this.filteredItems.findIndex(createFindByItemId(item));

        // Adding items between previous item and selected item
        if (selectedItemIndex > previousItemIndex) {
          for (let i = previousItemIndex; i <= selectedItemIndex; i++) {
            this.itemCheckChange({ item: this.filteredItems[i] }, checked);
          }
        } else {
          for (let i = previousItemIndex; i >= selectedItemIndex; i--) {
            this.itemCheckChange({ item: this.filteredItems[i] }, checked);
          }
        }
      } else {
        // Default case, just add or remove item
        this.itemCheckChange({ item }, checked);
      }

      // Assume that all the above resulted in a change of the selected files
      this.refreshSelectionForm();
    },
    /**
     * Update check status's item
     * @param {Object} thumnailProps.item - the item rendered that changed
     * @param {Boolean} checked - if this is now checked
     * @returns {void}
     */
    itemCheckChange({ item }, checked) {
      const selectedFileIndex = this.selectedFiles.findIndex(createFindByItemId(item));
      if (checked && selectedFileIndex === -1) {
        this.selectedFiles.push(item);
      } else if (!checked && selectedFileIndex !== -1) {
        this.selectedFiles.splice(selectedFileIndex, 1);
      }
      // Whenever we make a change to the selection, open the panel
      this.showSelectionPanel = true;
      this.$emit('changeSelectedFiles', [...this.selectedFiles]);
    },
    /**
     * Clears the selected files
     *
     * @returns {void}
     * */
    resetSelectedItems() {
      this.selectedFiles = [];
      this.showSelectionPanel = false;
      this.$emit('resetSelectedFiles');

      this.refreshSelectionForm();
    },
    /**
     * Toggles 'showSelectionPanel'
     *
     * @returns {void}
     */
    toggleShowSelectionPanel() {
      const nextValue = !this.showSelectionPanel;

      // If we are going to show the selection panel, clear the info items
      // while checking for unsaved changes
      if (nextValue) {
        this.executeConfirmableAction(() => {
          this.resetSelectedInfoItems();
          this.showSelectionPanel = nextValue;
        }, PanelTypes.INFO);
      } else {
        this.showSelectionPanel = nextValue;
      }
    },
    /**
     * Clears the selected information files
     *
     * @returns {void}
     */
    resetSelectedInfoItems() {
      this.selectedFilesForInfos = [];

      this.refreshInformationForm();
    },
    /**
     * Hook when an item's information was check
     *
     * @param {Object} item - the item to show info for
     * @param {ClickEvent} event - the event that triggered the change
     * @returns {void}
     */
    handleClickItemInfoHook(item, event) {
      // Check if selection is unsaved
      // We want to check the unsaved selection individually of unsaved info,
      // so that if we need to display a confirmation dialog, it will always
      // correspond to the current panel
      this.executeConfirmableAction(() => {
        // Then modify our info, while still checking if the current
        // info is unsaved
        this.executeConfirmableAction(() => {
          this.handleClickItemInfo(item, event);
        }, PanelTypes.INFO);
      }, PanelTypes.SELECTION);
    },
    /**
     * Updates the current info item
     *
     * @param {Object} item - the item to show info for
     * @param {ClickEvent} event - the event that triggered the change
     * @returns {void}
     */
    handleClickItemInfo(item, event) {
      const itemIndex = this.selectedFilesForInfos.findIndex(createFindByItemId(item));
      if (event && (event.ctrlKey || event.metaKey || event.shiftKey)) {
        const hasExistingSelection = this.selectedFilesForInfos.length;

        // Can only use 'shift' from previous to current file, if there
        // is at least one selected info item
        if (event.shiftKey && hasExistingSelection) {
          const lastInfoItem = this.selectedFilesForInfos[this.selectedFilesForInfos.length - 1];
          const previousItemIndex = this.filteredItems.findIndex(createFindByItemId(lastInfoItem));
          const selectedItemIndex = this.filteredItems.findIndex(createFindByItemId(item));

          // Select the items between the last selected info item and
          // the clicked one, if the click one was not selected
          const willBeSelected = itemIndex === -1;

          // Adding items between previous item and selected item
          if (selectedItemIndex > previousItemIndex) {
            for (let i = previousItemIndex; i <= selectedItemIndex; i++) {
              this.setItemInfoSelection(this.filteredItems[i], willBeSelected);
            }
          } else {
            for (let i = previousItemIndex; i >= selectedItemIndex; i--) {
              this.setItemInfoSelection(this.filteredItems[i], willBeSelected);
            }
          }
        } else {
          // Else 'ctrl' case, or no previous item
          this.setItemInfoSelection(item, itemIndex === -1);
        }
        // Whenever the info items change, open the panel
        this.showInfoPanel = true;
      } else if (itemIndex === -1 || (itemIndex >= 0 && this.selectedFilesForInfos.length > 1)) {
        // If the item wasn't selected
        // OR
        // was selected, but NOT the only one
        // => clear selection, then select the item
        this.selectedFilesForInfos = [item];
        // Whenever the info items change, open the panel
        this.showInfoPanel = true;
      }

      // Assume that all the above resulted in a change of the
      // info files
      this.refreshInformationForm();
    },
    /**
     * Update item info selection status
     *
     * @param {Object} item - the file whose info selection to change
     * @param {Boolean} selected - if the file is now selected
     * @returns {void}
     */
    setItemInfoSelection(item, selected) {
      const itemIndex = this.selectedFilesForInfos.findIndex(createFindByItemId(item));
      if (selected && itemIndex === -1) {
        this.selectedFilesForInfos.push(item);
      } else if (!selected && itemIndex !== -1) {
        this.selectedFilesForInfos.splice(itemIndex, 1);
      }
    },
    /**
     * Updates the zoomed item
     *
     * @param {Object} item - the item rendered
     * that should be zoomed
     * @param {ClickEvent} event - the event that triggered the change
     * @returns {void}
     */
    handleClickItemZoom(item, itemIndex, event) {
      const zoomedItemIsDifferentFromInfoItems =
        this.selectedFilesForInfos.length !== 1 ||
        (this.selectedFilesForInfos.length === 1 &&
          this.selectedFilesForInfos[0].itemId !== item.itemId);

      const openZoomItem = () => {
        this.$refs.previewAssetDialog.open(this.filteredItems, itemIndex);
      };

      // First verify that the selection doesn't have unsaved changes
      this.executeConfirmableAction(() => {
        // Then if we know that we are changing info item, manually check
        // for unsaved changes
        if (zoomedItemIsDifferentFromInfoItems) {
          this.executeConfirmableAction(openZoomItem, PanelTypes.INFO);
        } else {
          // Otherwise, we are zooming the item that is already the only info item
          // So we can simply open the zoom dialog, even if there are unsaved changes
          openZoomItem();
        }
      }, PanelTypes.SELECTION);
    },
    /**
     * Updates the zoomed item id when the zoom dialog changes
     * the item it's zoomed in on
     *
     * @param {object} file - the current zoomed file
     * @returns {void}
     */
    onChangeZoomedItem(file) {
      // Only refresh the zoom form if it was already showing an old form
      if (this.zoomedItemId && file) {
        this.refreshZoomForm();
      }

      this.zoomedItemId = file ? file.itemId : null;

      // If we changed the zoomed item to a new item, set it as the only info file
      // We assume that we already checked for unsaved changes before the change
      // of the zoomed file
      // If we changed the item to nothing (exit zoom), keep the info file
      if (file) {
        const zoomFileWasAlreadyInfo =
          this.selectedFilesForInfos.length && this.selectedFilesForInfos[0].itemId === file.itemId;

        this.selectedFilesForInfos = [file];

        if (!zoomFileWasAlreadyInfo) {
          this.refreshInformationForm();
        }
      }
    },
    /**
     * Updates the dragging files when a file drag is started
     * and sets the drag preview
     *
     * @param {object} item - the file that was started to drag
     * @returns {void}
     */
    handleItemDragStart(item, event) {
      // If we started dragging a checked file, we actually want
      // to drop all the currently selected files
      if (this.isItemChecked(item)) {
        this.draggingFiles.push(...this.selectedFiles);
        event.dataTransfer.setDragImage(this.selectionDragIconDiv, DRAG_ICON_DIM, DRAG_ICON_DIM);
      } else {
        // Otherwise, even if other files are selected, we only
        // want to drop the file we stated dragging
        this.draggingFiles.push(item);
        event.dataTransfer.setDragImage(this.singleDragIconDiv, DRAG_ICON_DIM, DRAG_ICON_DIM);
      }
    },
    /**
     * Creates a div that contains the drag icon. The div will be used
     * for the drag preview
     *
     * @param {*} dragIconEl - vue DragIcon component that contains the svg
     * of the drag icon
     * @returns {HTMLDivElement} the div icon
     */
    createDragIcon(dragIconEl) {
      const dragIcon = dragIconEl.$el.outerHTML
        .replace(/visibility:\s?hidden;/, '')
        .replace(/position:\s?absolute;/, '')
        .replace(/top:\s?\d+px;/, '');

      // Have wrapped it inside a DIV, as I've noticed
      // on the internet a lot of issues resolved by doing so.
      // (your choice)
      const div = document.createElement('div');
      const img = document.createElement('img');
      img.src = 'data:image/svg+xml;base64,' + base64EncodeUnicode(dragIcon);
      img.width = DRAG_ICON_DIM;
      img.height = DRAG_ICON_DIM;
      div.style.position = 'absolute';

      // For Chrome, we need to add the Image to the DOM Also....
      // @see (https://stackoverflow.com/questions/43790022/html5-draggable-setdragimage-doesnt-work-with-canvas-on-chrome)
      div.style.zIndex = 1000;
      div.style.top = '-1000px';
      div.appendChild(img);
      div.style.width = '48px';
      div.style.height = '48px';
      document.body.appendChild(div);

      return div;
    },
    /**
     * Updates the dragging files when a file drag is stopped
     *
     * @param {object} item - the file that was started to drag
     * @returns {void}
     */
    handleItemDragEnd(item) {
      this.draggingFiles = [];
    },
    /**
     * @param {object} item - the item to get dimensions for
     * @returns {object} object with width & height for masonry
     * to use
     */
    getItemDimensions(item) {
      return {
        width: item.description.width,
        height: item.description.height,
      };
    },
    /**
     * Opens a sub Assets component for the selected files
     *
     * @returns {void}
     */
    openSelectionDialog() {
      this.$emit('openSelectionDialog', [...this.selectedFiles]);
    },
    /**
     * Closes the selected files dialog
     *
     * @returns {void}
     */
    closeSelectionDialog() {
      // TODO: close sub Assets dialog
    },
    // SEARCH
    /**
     * Triggered when the search items changed
     *
     * @returns {void}
     */
    onSearchItemsChange() {
      this.isSearchDirty = true;
    },
    /**
     * Updates our search filters open value
     *
     * @param {object[]} searchItems - array of search items
     * @returns {void}
     */
    searchFiltersOpened(searchItems) {
      this.searchFiltersAreOpen = true;
    },
    /**
     * Updates our search filters open value
     *
     * @param {object[]} searchItems - array of search items
     * @returns {void}
     */
    searchFiltersClosed(searchItems) {
      this.searchFiltersAreOpen = false;

      this.triggerSearchIfDirty(searchItems);
    },
    /**
     * Triggers a search if the filters are closed
     *
     * @param {object[]} searchItems - array of search items
     * @returns {void}
     */
    onSearchItemRemoved(searchItems) {
      if (!this.searchFiltersAreOpen) {
        // Since we just removed an item, we know we can
        // force a refresh
        this.triggerSearchForced(searchItems);
      }
    },
    /**
     * Executes a confirmable info callback
     *
     * @param {function} callback - the function to execute once
     * we know we can change the info file selection
     * @returns {void}
     */
    executeConfirmableInfoAction(callback) {
      this.executeConfirmableAction(callback, PanelTypes.INFO);
    },
    /**
     * Trigerred when the search bar should trigger an update.
     * Forces a refresh even if the items did not change
     *
     * @param {object[]} searchItems - array of search items
     * @returns {void}
     */
    triggerSearchForced(searchItems) {
      this.triggerSearchIfDirty(searchItems, true);
    },
    /**
     * Trigerred when the search bar should trigger an update.
     * Will only search if the items did change (`this.isSearchDirty`) or `refreshWhenNotDirty`
     *
     * @param {object[]} searchItems - array of search items
     * @param {boolean} [refreshWhenNotDirty=false] - if should search, even when not dirty
     * @returns {void}
     */
    triggerSearchIfDirty(searchItems, refreshWhenNotDirty = false) {
      this.searchItems = searchItems;

      if (this.isSearchDirty || refreshWhenNotDirty) {
        this.loadData(true);
      }
    },
    // DETAILS/INFO panels
    /**
     * Will show a confirmation dialog if any of of the selection/info
     * panels have dirty attributes. On save/ignore, the callback will be invoked
     *
     * @param {() => void} actionCallback - the function to call after the user indicates to discard
     * the unsaved changes, or after the changes are saved, or if there is nothing to save at all
     * @param {string} panelType - the panel to check for unsaved attributes
     */
    executeConfirmableAction(actionCallback, panelType) {
      if (panelType === PanelTypes.SELECTION || panelType === PanelTypes.INFO) {
        this.$refs.detailsPanel.executeConfirmableAction(actionCallback, panelType);
        return;
      }
      if (panelType === PanelTypes.ZOOM) {
        this.$refs.previewAssetDialog.executeConfirmableAction(actionCallback);
      }
    },
    /**
     * Updates the selection form
     *
     * @returns {void}
     */
    async refreshSelectionForm() {
      // Wait for details panel to receive new files via props
      await this.$nextTick();
      if (this.$refs.detailsPanel) {
        return this.$refs.detailsPanel.refreshSelectionForm();
      }
    },
    /**
     * Updates the information form
     *
     * @returns {void}
     */
    async refreshInformationForm() {
      // Wait for details panel to receive new files via props
      await this.$nextTick();
      if (this.$refs.detailsPanel) {
        return this.$refs.detailsPanel.refreshInformationForm();
      }
    },
    /**
     * Updates the zoom form
     *
     * @returns {void}
     */
    refreshZoomForm() {
      // Wait for details panel to receive new files via props
      this.$nextTick(() => {
        if (this.$refs.previewAssetDialog) {
          this.$refs.previewAssetDialog.refreshZoomForm();
        }
      });
    },
    // FOLDER TREE
    /**
     * Triggered when a folder sort is selected
     *
     * @param {object} sort - the selected sort object
     */
    onClickFolderSort(sort) {
      if (
        this.selectedFolderSort &&
        this.selectedFolderSort.sortKey === sort.sortKey &&
        this.selectedFolderSort.direction === sort.direction
      ) {
        return;
      }
      this.selectedFolderSort = sort;
      if (this.$refs.folderSorts) {
        this.$refs.folderSorts.close();
      }
    },
    /**
     * Retrieves the dam root folders
     *
     * @param {object} sort - the selected sort
     * @returns Promise<object[]>
     */
    async getDamRootFolders(sort) {
      const res = await getDamService('foldersService').getRootFolders('fileAssets', sort);
      return res.body.items;
    },
    onFolderSelectionChange(selection) {
      this.folderSelection = selection;
    },
    /**
     * Updates our folder selection state to match the
     * new one from the tree
     *
     * @param {object} selectionState - object map of the folders' states
     * @returns {void}
     */
    onFolderSelectionStateChange(selectionState) {
      this.folderSelectionState = {
        ...selectionState,
      };

      if (this.fileMode === FileMode.NORMAL) {
        this.loadData(true);
      }

      if (this.$refs.fileSearch) {
        this.$refs.fileSearch.setDirtySearchPreview();
      }
    },
    /**
     * Handles a tree error
     *
     * @param {string} level - the error node level
     * @param {Error} error - the error that occurred
     * @returns {void}
     */
    handleTreeError(level, error) {
      if (level === 'root') {
        this.$router.push({
          name: 'dam-error',
          query: { back: this.$router.currentRoute.name },
        });
      }
    },
    /**
     * Handles drag start of a folder
     *
     * @param {object} draggingFolder - the dragging folder
     * @param {*} e - drag start event
     */
    handleTreeDragStart(draggingFolder, e) {
      this.draggingFolder = draggingFolder;

      e.dataTransfer.setDragImage(this.folderDragIconDiv, DRAG_ICON_DIM, DRAG_ICON_DIM);
    },
    /**
     * Handles drag end of a folder
     */
    handleTreeDragEnd() {
      this.draggingFolder = null;
    },
    /**
     * Handles a drop on a folder in the FolderTree.
     * Opens the associate dialog
     *
     * @param {object} folder - the folder that was dropped in
     * @returns {void}
     */
    handleTreeDrop(folder) {
      if (this.draggingFiles.length) {
        if (!this.hasPermission(Permissions.Files.MOVE)) {
          services
            .getService('alerts')
            .alertError(this.$t('dam.files.move.alert.no_move_permission'));
        } else if (this.$refs.associateDialog) {
          // Copy array so we don't mutate the data
          this.$refs.associateDialog.open([...this.draggingFiles], {
            ...folder,
          });
        }
      }
    },
    /**
     * @param {object} node - the node on which the drag is over
     * @param {*} e - the dragover event
     * @returns {boolean} if the node should apply drag over styles
     */
    onTreeDragOver(node, e) {
      if (!this.draggingFiles.length) {
        return false;
      }
      // Only apply drag over styles when dragging a file
      // (enable folder drop on folder later on)

      e.preventDefault(); // Allows drop
      e.dataTransfer.dropEffect = 'move';
      return true;
    },
    /**
     * @param {object} node - the node on which the drag entered
     * @param {*} e - the dragenter event
     * @returns {boolean} if the node should apply drag over styles
     */
    onTreeDragEnter(node, e) {
      // Only apply drag over styles when dragging a file
      // (enable folder drop on folder later on)
      return !!this.draggingFiles.length;
    },
    /**
     * Opens the folder create dialog with the selected folder to create a child
     * @param {object[]} [selectedFolder=null] - the folder to create a child of. Leave null
     * to create a root folder
     */
    openFolderCreateDialog(selectedFolder = null) {
      if (this.$refs.folderCreateDialog) {
        this.$refs.folderCreateDialog.open(selectedFolder);
      }
    },
    /**
     * Callback to trigger when a folder is inserted in the tree
     *
     * @param {object} folder - the plain created folder dto
     * @param {string|null} parentId - id of the parent on which
     * we created the folder
     */
    async onFolderCreated(folder, parentId) {
      if (!parentId) {
        // Do this async
        this.$refs.folderTree.refreshRoots();
      } else if (parentId) {
        // Do this async
        this.$refs.folderTree.refreshFolder(parentId);
      }
    },
    /**
     * @param {object} folder - a folder in the tree
     * @returns {Number} the amount of selected files whose paths
     * contain the folder
     */
    getFolderSelectionCount(folder) {
      let count = 0;
      this.selectedFiles.forEach(file => {
        if (file.paths.some(path => path.includes(folder.itemId))) {
          count++;
        }
      });

      return count;
    },
    /**
     * @returns {object} object of listerners forthe node's action popper
     */
    nodeActionPopperListeners(node) {
      return {
        open: () => {
          this.$set(node, '_isPopperOpen', true);
        },
        close: () => {
          this.$set(node, '_isPopperOpen', false);
        },
      };
    },
    // FILE UPLOAD
    /**
     * Opens the file upload dialog
     *
     * @returns {void}
     */
    openFileUploadDialog() {
      if (this.$refs.fileUploadDialog) {
        // Copy array
        this.$refs.fileUploadDialog.open([...this.folderSelection]);
      }
    },
    /**
     * Navigates to a folder where files have been uploaded to
     *
     * @param {object} folder - the folder that has been uploaded to
     * @param {string} folder.itemId - the id of the folder
     * @param {string[]} folder.paths - the paths to this folder
     * of the upload tree
     */
    async navigateToUploadedFolder(folder) {
      // Update this so we can launch a search right now
      this.folderSelection = [{ node: folder, state: SelectionState.MeOnly }];
      this.loadData(true); // Async

      // Clear the current selection, since we want to only select the target
      this.$refs.folderTree.clearNodeSelectionState();

      this.$refs.folderTree.setNodeIdSelectionState(folder.itemId, SelectionState.MeOnly, false);

      const folderPath = folder.paths.length ? folder.paths[0] : null;
      if (!folderPath || !folderPath.length) {
        this.folderSelection = this.$refs.folderTree.getFolderSelection();
        this.folderSelectionState = this.$refs.folderTree.getFolderSelectionState();

        // Cannot navigate to folder if it has no path
        return;
      }

      this.isExpandingToUploadedFolder = true;

      const fullFolderIds = getFolderIdsFromPath(folderPath);
      const parentFolderIds = fullFolderIds.slice(0, Math.max(fullFolderIds.length - 1, 0)); // Exlude the last folder

      for (const folderId of parentFolderIds) {
        // For ancestor nodes of the upload root, we can preemptively select them
        // as 'not me, but some children' since we know the target is a descendant,
        // and they themselves cannot be checked
        this.$refs.folderTree.setNodeIdSelectionState(
          folderId,
          SelectionState.NotMeButSomeChildren,
          false
        );

        // For every folder in the path, expand it and load children
        await this.$refs.folderTree.expandFolder(folderId, fullFolderIds);
      }

      // Once everything is done, manually update the folder selection
      // without reloading the files in the gallery
      this.folderSelection = this.$refs.folderTree.getFolderSelection();
      this.folderSelectionState = this.$refs.folderTree.getFolderSelectionState();

      this.isExpandingToUploadedFolder = false;
    },
  },
  mounted() {
    services
      .getService('uploadManager')
      .$on(FileUploadEvents.OPEN_FILE_UPLOAD_DIALOG, this.openFileUploadDialog);
    services
      .getService('uploadManager')
      .$on(FileUploadEvents.NAVIGATE_TO_FOLDER, this.navigateToUploadedFolder);

    getDamService('filesService').$on(
      FileGalleryEvents.REFRESH_SEARCH,
      this.reloadSearchFromBeginning
    );

    this.$nextTick(() => {
      // Create the single file & folder drag icon divs on mount
      // since their props count will never change
      this.singleDragIconDiv = this.createDragIcon(this.$refs.singleDragIcon);
      this.folderDragIconDiv = this.createDragIcon(this.$refs.folderDragIcon);
    });
  },
  beforeDestroy() {
    services
      .getService('uploadManager')
      .$off(FileUploadEvents.OPEN_FILE_UPLOAD_DIALOG, this.openFileUploadDialog);
    services
      .getService('uploadManager')
      .$off(FileUploadEvents.NAVIGATE_TO_FOLDER, this.navigateToUploadedFolder);

    getDamService('filesService').$off(
      FileGalleryEvents.REFRESH_SEARCH,
      this.reloadSearchFromBeginning
    );
  },
};
</script>
